import { createContext, useContext, useEffect, useState } from "react";
import { translations } from "@/i18n/translations";

const I18nContext = createContext(null);

const STORAGE_KEY = "prieure_locale";

// All 19 supported locales. RTL_LOCALES flip the layout direction.
export const SUPPORTED_LOCALES = [
  "fr", "en", "it", "es", "de", "pt", "zh", "pl", "ru", "uk", "ro",
  // Wave 2 (2026-02-18): 6 new locales
  "is", "ar", "hi", "ta", "ko", "ja",
  // Wave 3 (2026-02-19): Nordic languages
  "sv", "nb",
];
export const RTL_LOCALES = ["ar"];

export function I18nProvider({ children }) {
  const [locale, setLocale] = useState(() => {
    if (typeof window !== "undefined") {
      try {
        const urlLang = new URLSearchParams(window.location.search).get("lang");
        if (urlLang && SUPPORTED_LOCALES.includes(urlLang)) {
          window.localStorage.setItem(STORAGE_KEY, urlLang);
          return urlLang;
        }
      } catch { /* ignore */ }
    }
    const saved = typeof window !== "undefined" ? window.localStorage.getItem(STORAGE_KEY) : null;
    if (SUPPORTED_LOCALES.includes(saved)) return saved;
    return "fr";
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, locale);
      document.documentElement.lang = locale;
      // Toggle document direction for RTL locales (Arabic).
      document.documentElement.dir = RTL_LOCALES.includes(locale) ? "rtl" : "ltr";
    }
  }, [locale]);

  // Deep-merge translations with FR fallback so partial translations work
  const baseFr = translations.fr;
  const target = translations[locale] || {};
  const t = locale === "fr" ? baseFr : deepMerge(baseFr, target);
  const toggle = () => setLocale((l) => (l === "fr" ? "en" : "fr"));

  // tr({fr,en,it,es,de}) — picks the current locale, falling back through EN -> FR.
  const tr = (dict) => {
    if (!dict || typeof dict !== "object") return "";
    return dict[locale] || dict.en || dict.fr || "";
  };

  return (
    <I18nContext.Provider value={{ locale, setLocale, toggle, t, tr }}>
      {children}
    </I18nContext.Provider>
  );
}

function deepMerge(a, b) {
  if (Array.isArray(a)) return Array.isArray(b) ? b : a;
  if (a && typeof a === "object" && b && typeof b === "object") {
    const out = { ...a };
    for (const k of Object.keys(b)) out[k] = deepMerge(a[k], b[k]);
    return out;
  }
  return b !== undefined ? b : a;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside I18nProvider");
  return ctx;
}
